import React, {
	Suspense,
	useState,
	useCallback,
	useEffect,
	useMemo,
} from "react"
import base64 from "base-64"
import iconv from "iconv-lite"
import queryString from "query-string"
import { throttle } from "lodash"
import { ToastContainer, toast } from "react-toastify"

import AboutModal from "./components/AboutModal"
import Composition from "./components/Composition"
import { WebGLComposition, TextureLoader } from "./components/WebGLComposition"

import Input from "./components/Input"
import { Sounds } from "./Sounds"

import images from "./content/round_7_images"

import { SPEED } from "./config"

import { useInterval } from "./hooks/useInterval"

import { ReactComponent as InfoSVG } from "./assets/help.svg"
import { ReactComponent as ShareSVG } from "./assets/share.svg"
import { ReactComponent as VolumeMuteSVG } from "./assets/volume_off.svg"
import { ReactComponent as VolumeUpSVG } from "./assets/volume_up.svg"
import { ReactComponent as PlaySVG } from "./assets/play.svg"

// import { ReactComponent as LoaderSVG } from "./assets/loader.svg"

import "./App.css"
import "./toastify.css"

import { Splash } from "./Splash"

function updateURL(seed) {
	const buf = iconv.encode(seed, "ISO-8859-8")
	const encoded = encodeURIComponent(base64.encode(buf))
	// eslint-disable-next-line no-restricted-globals
	history.replaceState(null, "", `?query=${encoded}`)
}

const updateURLThrottled = throttle(updateURL, 1000)

function Loader({ isVisible }) {
	return <div className="LoadingText">{isVisible && "Loading ..."}</div>
}

export function VolumeToggle({ isActive, onClick }) {
	return (
		<div className="VolumeButton" onClick={onClick}>
			{!isActive ? <VolumeMuteSVG /> : <VolumeUpSVG />}
		</div>
	)
}

export default function App() {
	const [isSoundActive, setSoundActive] = useState(false)
	const [isSoundAllowed, setSoundAllowed] = useState(true)
	const [isFocused, setFocused] = useState(false)
	const [seed, setSeed] = useState("")
	const [message, setMessage] = useState("")
	const [areTexturesLoaded, setTexturesLoaded] = useState(false)
	const [areSoundsLoaded, setSoundsLoaded] = useState(false)
	const [isAnimating, setAnimating] = useState(false)
	const [isModalOpen, setModalOpen] = useState(false)
	const [isSplashDismissed, setSplashDismissed] = useState(false)

	const isReady = useMemo(
		() => (areTexturesLoaded && areSoundsLoaded) || isAnimating,
		[areSoundsLoaded, areTexturesLoaded, isAnimating],
	)

	const handleChange = useCallback((e) => {
		const newSeed = e.target.value
		updateURLThrottled(newSeed)
		setMessage(newSeed)
		setSeed(newSeed)
		setAnimating(false)
	}, [])

	const handleShareClick = useCallback(async (e) => {
		await navigator.clipboard.writeText(window.location.href)

		toast.info("Copied URL to clipboard", {
			hideProgressBar: true,
			position: toast.POSITION.TOP_RIGHT,
		})
	}, [])

	const handleSoundClick = useCallback(() => {
		if (isSoundActive) {
			setSoundActive(null)
		} else {
			setSoundActive(true)
		}
	}, [isSoundActive])

	const handleTextureLoad = useCallback(() => {
		setTexturesLoaded(true)
	}, [])

	const handleAudioLoad = useCallback(() => {
		setSoundsLoaded(true)
	}, [])

	const handleSplashDismissal = useCallback(
		(e) => {
			setSplashDismissed(true)
			isSoundAllowed && setSoundActive(true)
		},
		[isSoundAllowed],
	)

	const handleInputClick = useCallback(
		(e) => {
			if (isAnimating) {
				setAnimating(false)
				setSeed(message)
			}
		},
		[isAnimating, message],
	)

	const handlePlayClick = useCallback((e) => {
		setSeed("")
		setAnimating(true)
	}, [])

	useEffect(() => {
		if (isSplashDismissed) {
			if (areTexturesLoaded) {
				if (isSoundAllowed) {
					if (areSoundsLoaded) {
						setAnimating(true)
						// setTimeout(() => setAnimating(true), 666)
					}
				} else {
					setAnimating(true)
					// setTimeout(() => setAnimating(true), 666)
				}
			}
		}
	}, [areTexturesLoaded, areSoundsLoaded, isSoundAllowed, isSplashDismissed])

	useEffect(() => {
		const params = queryString.parse(window.location.search)
		if (!params.query) return
		const unescaped = decodeURIComponent(params.query)
		const decoded = base64.decode(iconv.decode(unescaped, "ISO-8859-8"))
		setMessage(decoded)
	}, [])

	useInterval(() => {
		if (isAnimating && seed.length < message.length) {
			const newSeed = seed + message[seed.length]
			setSeed(newSeed)
			updateURLThrottled(newSeed)
			if (newSeed === message) {
				setAnimating(false)
			}
		}
	}, SPEED)

	return (
		<div className="App">
			<AboutModal isOpen={isModalOpen} setOpen={setModalOpen} />
			<ToastContainer />

			{!isSplashDismissed && (
				<Splash
					hasMessage={message.length > 0}
					isSoundAllowed={isSoundAllowed}
					setSoundAllowed={setSoundAllowed}
					onConfirm={handleSplashDismissal}
				/>
			)}

			<Composition
				seed={seed}
				images={images}
				className={isFocused ? "hidden-on-mobile" : ""}
			>
				{(elements) => (
					<>
						{elements ? (
							<Suspense fallback={<Loader isVisible={isSplashDismissed} />}>
								<TextureLoader elements={elements} onLoad={handleTextureLoad}>
									{({ textures }) => (
										<WebGLComposition
											elements={elements}
											seed={seed}
											textures={textures}
										/>
									)}
								</TextureLoader>
							</Suspense>
						) : null}
					</>
				)}
			</Composition>

			<Sounds isActive={isSoundActive} onLoad={handleAudioLoad} seed={seed} />

			{isReady && isSplashDismissed && (
				<Input
					value={seed}
					onChange={handleChange}
					onClick={handleInputClick}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					hasMessage={message.length > 0}
				/>
			)}

			{isSplashDismissed && isReady && (
				<div className={`actions ${isFocused ? "hidden-on-mobile" : ""}`}>
					<InfoSVG
						className="InfoButton"
						onClick={(e) => setModalOpen(true)}
						title="More info"
					/>

					<VolumeToggle
						onClick={handleSoundClick}
						isActive={isSoundActive}
						title="Toggle audio"
					/>

					<div className="stretch" />

					{!isAnimating && (
						<div
							className="PlayButton"
							onClick={handlePlayClick}
							title="Replay message"
						>
							<PlaySVG />
							{/* <span>Replay</span> */}
						</div>
					)}

					<div
						className="ShareButton"
						onClick={handleShareClick}
						title="Share message"
					>
						<ShareSVG />
						{/* <span>Share</span> */}
					</div>
				</div>
			)}
		</div>
	)
}
